<script setup>
import { ref, reactive, onMounted } from "vue";
import {
  getOpKnowledgeBaseCategoryTreeApi,
  getArticlePageListApi,
  getArticleInfoByIdApi,
  deleteArticleByIdApi,
  updateArticleStatusApi,
} from "@/api/article";
import { ElMessage, ElMessageBox } from "element-plus";
import Adjust from "./adjust.vue";
import { keyBy } from "lodash";
import { Loading } from "element-plus/es/components/loading/src/service";

// 页面标题
const title = ref("文章维护");

// 搜索条件ref
const searchRef = ref(null);

// 编辑组件ref
const adjustRef = ref(null);

// 搜索条件
const searchData = reactive({
  title: null,
  startTime: null,
  endTime: null,
  isCommonProblem: null,
  status: null,
  categoryId: null,
});

//el-switch绑定值
const inactiveValue = ref(0);
const activeValue = ref(1);

//搜索时间
const timer = ref("");

//搜索时间变化
const changeDate = (val) => {
  console.log(val);
  if (val) {
    searchData.startTime = val[0];
    searchData.endTime = val[1];
  }
};

//指定时间
const defaultTime = reactive([
  new Date(0, 0, 0, 0, 0, 0),
  new Date(0, 0, 0, 23, 59, 59),
]);

//状态
const statusList = reactive([
  { key: 0, label: "停用" },
  { key: 1, label: "启用" },
]);

//常见问题
const questionList = reactive([
  { key: 0, label: "否" },
  { key: 1, label: "是" },
]);

// 树props
const defaultProps = reactive({
  label: "name",
  children: "childNodes",
});

// 区域树
const treeData = ref([]);

// 获取树
const getDirectoryTree = () => {
  getOpKnowledgeBaseCategoryTreeApi()
    .then((res) => {
      treeData.value = res.data;
    })
    .finally(() => {});
};

// 分页配置
const pages = reactive({
  page: 1,
  limit: 10,
  total: 0,
});

// 表格loading
const tableLoading = ref(false);

// 表格列配置
const columns = ref([
  {
    label: "标题",
    prop: "title",
  },
  {
    label: "创建用户",
    prop: "createByName",
  },
  {
    label: "创建时间",
    prop: "createTime",
  },
]);

// 表格数据
const tableData = ref([]);

// 获取数据
const getList = () => {
  tableLoading.value = true;
  let submitData = { ...searchData };
  if (submitData.title == "") {
    submitData.title = null;
  }
  getArticlePageListApi({
    ...submitData,
    page: pages.page,
    limit: pages.limit,
  })
    .then((res) => {
      tableData.value = res.data.records;
      pages.total = res.data.total;
    })
    .finally(() => {
      tableLoading.value = false;
    });
};

// 重置
const searchReset = () => {
  console.log(searchRef);
  searchRef.value?.resetFields();
  searchData.startTime = null;
  searchData.endTime = null;
  timer.value = null;
  getList();
};

// 点击新增、编辑
const handleAdjust = (row, type) => {
  adjustRef.value?.handleDialogOpen(row, type);
};

//点击切换状态
const handleChangeStatus = (row) => {
  ElMessageBox.confirm(
    `确定切换为${row.status == "0" ? "停用" : "启用"}状态?`,
    "警告",
    {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "confirm",
    }
  )
    .then(() => {
      updateArticleStatusApi(row.id, row.status)
        .then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            getList();
          }
        })
        .catch(() => {
          ElMessage.error("操作失败 ");
          row.status = row.status == "0" ? "1" : "0";
        });
    })
    .catch(() => {
      row.status = row.status == "0" ? "1" : "0";
    });
};

// 点击删除
const handleDelete = (row) => {
  ElMessageBox.confirm(`确定删除${row.title}?`, "警告", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "error",
  })
    .then(() => {
      tableLoading.value = true;
      deleteArticleByIdApi(row.id).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("操作成功");
          getList();
          tableLoading.value = false;
        }
      });
    })
    .catch(() => {
      tableLoading.value = false;
    });
};

onMounted(() => {
  getList();
  getDirectoryTree();
});
</script>

<template>
  <zh-table-page v-bind="{ title }">
    <template #header-btn>
      <el-button type="primary" icon="Plus" @click="handleAdjust(null, 'add')"
        >新增</el-button
      >
    </template>
    <template #search>
      <el-form ref="searchRef" :model="searchData" :inline="true">
        <el-form-item label="标题" prop="title">
          <el-input v-model.trim="searchData.title" placeholder="请输入标题" />
        </el-form-item>
        <el-form-item label="创建时间">
          <el-date-picker
            format="YYYY/MM/DD"
            value-format="YYYY-MM-DD HH:mm:ss"
            v-model="timer"
            is-range
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="changeDate"
            :default-time="defaultTime"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="常见问题" prop="isCommonProblem">
          <el-select
            v-model="searchData.isCommonProblem"
            placeholder="请选择是否为常见问题"
          >
            <el-option
              v-for="item in questionList"
              :key="item.key"
              :label="item.label"
              :value="item.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="searchData.status" placeholder="请选择状态">
            <el-option
              v-for="item in statusList"
              :key="item.key"
              :label="item.label"
              :value="item.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="目录" prop="categoryId">
          <el-tree-select
            check-strictly
            v-model="searchData.categoryId"
            :data="treeData"
            :render-after-expand="false"
            :props="defaultProps"
            node-key="id"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="Search" @click="getList()"
            >查询</el-button
          >
          <el-button icon="Refresh" @click="searchReset()">重置</el-button>
        </el-form-item>
      </el-form>
    </template>
    <template #table>
      <el-table
        :header-cell-style="{ 'text-align': 'center' }"
        :cell-style="{ 'text-align': 'center' }"
        :data="tableData"
        v-loading="tableLoading"
      >
        <el-table-column type="index" label="序号" width="60" />
        <template v-for="item in columns" :key="item.prop">
          <el-table-column
            :label="item.label"
            :prop="item.prop"
            show-overflow-tooltip
          />
        </template>
        <el-table-column label="常见问题">
          <template #default="{ row }">
            <div>{{ row.isCommonProblem == 0 ? "否" : "是" }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态">
          <template #default="{ row }">
            <el-switch
              v-model="row.status"
              :inactive-value="inactiveValue"
              :active-value="activeValue"
              style="
                --el-switch-on-color: #13ce66;
                --el-switch-off-color: #dcdfe6;
              "
              active-text="启用"
              inactive-text="停用"
              @click="handleChangeStatus(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="{ row }">
            <el-button type="primary" link @click="handleAdjust(row, 'info')"
              >查看</el-button
            >
            <el-button type="primary" link @click="handleAdjust(row, 'edit')"
              >编辑</el-button
            >
            <el-button type="danger" link @click="handleDelete(row)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-show="pages.total > 0"
        :total="pages.total"
        v-model:current-page="pages.page"
        @update:current-page="getList"
        layout="->, prev, pager, next"
      />
    </template>
  </zh-table-page>
  <!-- 新增、编辑组件 -->
  <adjust ref="adjustRef" @afterSave="getList" />
</template>

<style lang="scss" scoped></style>
